// Tap a lead (v2.2): the twelve leads on our own ECG paper, two columns of six, each lead a cell big enough to tap on a
// phone (at least 46 CSS pixels tall at any width). The trace is calibrated as on the full sheet (25 mm/s, 10 mm/mV);
// the lead names are set over the canvas so they stay crisp and can change with the state. A tap picks; at the reveal
// the right leads are ringed in green with a tick, a wrong pick in red, and each cell shows the room's share.
// Without `pick` it is a still sheet with the leads named (the stage can use it to show the answer).
import { useMemo, useRef } from 'react';
import type { EcgGraphic } from '@shared/types';
import type { Lead } from './types';
import { drawLeadGrid, GRID_COLUMNS, leadCells, leadGridHeight, leadGridRow, LEAD_GRID_WIDTH } from './paper';
import { paperColours, paperParams, usePaint, useSheetSize } from './EcgPaper';
import { ecgKey } from './params';
import { LeadButtons, type LeadPick } from './LeadButtons';
import { canvasRatio, context2d } from '../env';
import '../clinical.css';

export type { LeadPick, LeadRegion } from './LeadButtons';

const columnOf = (lead: Lead) => (GRID_COLUMNS[0].includes(lead) ? 0 : 1);

export function LeadPicker({ graphic, pick, revealed = false, title }: { graphic: EcgGraphic; pick?: LeadPick; revealed?: boolean; title?: string }) {
  const key = ecgKey(graphic);
  // key stands for everything the sheet is drawn from
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => paperParams(graphic), [key]);
  const sizer = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { width, ratio } = useSheetSize(sizer);
  const scale = width > 0 ? width / LEAD_GRID_WIDTH : 0;
  const rowPx = leadGridRow(scale);
  const H = leadGridHeight(rowPx);
  usePaint(() => {
    const cv = canvas.current, ctx = context2d(cv);
    if (!cv || !ctx || width <= 0) return;
    drawLeadGrid(cv, ctx, { params, scale, rowPx, dpr: canvasRatio(ratio, width, H * scale), colours: paperColours() });
  }, [params, width, rowPx, ratio]);

  const name = title && title.trim() ? title : '12-lead ECG';
  const spoken = `${name}: twelve leads in two columns, limb leads on the left and chest leads on the right, 25 millimetres per second.${pick && !revealed ? ' Tap the lead you want.' : ''}`;

  return (
    <figure className="rx-leads" role="group" aria-label={spoken} data-revealed={revealed ? '' : undefined}>
      <div ref={sizer} className="rx-leads-sheet" style={{ aspectRatio: `${LEAD_GRID_WIDTH} / ${H}` }}>
        <canvas ref={canvas} aria-hidden="true" />
        <LeadButtons cells={leadCells(rowPx)} width={LEAD_GRID_WIDTH} height={H} pick={pick} revealed={revealed} columnOf={columnOf} />
      </div>
      <figcaption className="rx-paper-foot" aria-hidden="true"><span>{name} · 25 mm/s · 10 mm/mV</span></figcaption>
    </figure>
  );
}
